import React, { useState, useEffect } from "react";
import {
    Card,
    Breadcrumb,
    Divider,
    Button,
    Form,
    Input,
    Select,
    message,
    Space,
    Row,
    Col,
    Alert,
    Spin,
    Typography,
} from "antd";
import {
    HomeOutlined,
    BankOutlined,
    QrcodeOutlined,
    SaveOutlined,
    ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";

const { Option } = Select;
const { Text } = Typography;

export default function PaymentAccountCreate() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [bankList, setBankList] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [selectedBankCode, setSelectedBankCode] = useState(null);
    const [form] = Form.useForm();

    // Template options for VietQR
    const templateOptions = [
        {
            value: "compact2",
            label: "Compact 2 (540x640)",
            description: "Mã QR, logo, thông tin chuyển khoản",
        },
        {
            value: "compact",
            label: "Compact (540x540)",
            description: "QR kèm logo VietQR, Napas, ngân hàng",
        },
        {
            value: "qr_only",
            label: "QR Only (480x480)",
            description: "Chỉ bao gồm mã QR đơn giản",
        },
        {
            value: "print",
            label: "Print (600x776)",
            description: "Đầy đủ thông tin chuyển khoản",
        },
    ];

    // Fetch banks from VietQR API
    const fetchBanks = async () => {
        try {
            setLoadingBanks(true);
            const response = await fetch("https://api.vietqr.io/v2/banks");
            const result = await response.json();

            if (result.code === "00" && result.data) {
                setBankList(result.data);
            } else {
                message.error("Không thể tải danh sách ngân hàng");
            }
        } catch (error) {
            message.error("Lỗi khi tải danh sách ngân hàng: " + error.message);
        } finally {
            setLoadingBanks(false);
        }
    };

    useEffect(() => {
        fetchBanks();
    }, []);

    // Handle bank selection
    const handleBankSelect = (value) => {
        const selectedBank = bankList.find((bank) => bank.bin === value);
        setSelectedBankCode(selectedBank?.code);
        if (selectedBank) {
            form.setFieldsValue({
                acqId: selectedBank.bin,
                bank_name: (selectedBank.name || selectedBank.shortName) + "-" + selectedBank.code,
            });
        }
    };

    // Handle create account
    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const data = { ...values, bankcode: selectedBankCode };
            await paymentAccountAPIWithErrorHandling.create(data);
            message.success("Tạo tài khoản thành công!");
            navigate("/payment-account"); // Navigate to list or detail page
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <Card className="shadow-sm">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            {
                                href: "/",
                                title: (
                                    <>
                                        <HomeOutlined /> Trang chủ
                                    </>
                                ),
                            },
                            {
                                href: "/payment-account",
                                title: "Tài khoản thanh toán",
                            },
                            {
                                title: "Thêm mới",
                            },
                        ]}
                    />
                </div>

                <Divider />

                {/* Form */}
                <Spin spinning={loadingBanks} tip="Đang tải danh sách ngân hàng...">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark={false}
                        initialValues={{
                            type: "BANK",
                            template: "compact2",
                        }}
                    >
                        <Card
                            title={
                                <span className="text-blue-600">
                                    <BankOutlined className="mr-2" />
                                    Thêm tài khoản thanh toán mới
                                </span>
                            }
                            className="h-full shadow-sm border-blue-200"
                        >

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="name"
                                        label="Tên tài khoản"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Vui lòng nhập tên tài khoản",
                                            },
                                            { min: 2, message: "Tên phải có ít nhất 2 ký tự" },
                                        ]}
                                    >
                                        <Input placeholder="VD: Tài khoản chính, Tài khoản dự phòng..." />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="type"
                                        label="Loại thanh toán"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Vui lòng chọn loại thanh toán",
                                            },
                                        ]}
                                    >
                                        <Select placeholder="Chọn loại thanh toán">
                                            <Option value="BANK">
                                                <Space>
                                                    <BankOutlined />
                                                    Tài khoản ngân hàng
                                                </Space>
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="account_holder"
                                        label="Tên chủ tài khoản"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Vui lòng nhập tên chủ tài khoản",
                                            },
                                            { min: 2, message: "Tên phải có ít nhất 2 ký tự" },
                                        ]}
                                    >
                                        <Input placeholder="Nhập tên chủ tài khoản" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="account_number"
                                        label="Số tài khoản"
                                        rules={[
                                            { required: true, message: "Vui lòng nhập số tài khoản" },
                                        ]}
                                    >
                                        <Input placeholder="Nhập số tài khoản" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Chọn ngân hàng"
                                        rules={[
                                            { required: true, message: "Vui lòng chọn ngân hàng" },
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Tìm và chọn ngân hàng"
                                            optionFilterProp="children"
                                            onChange={handleBankSelect}
                                            filterOption={(input, option) =>
                                                (option?.label ?? "")
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                            loading={loadingBanks}
                                        >
                                            {bankList.map((bank) => (
                                                <Option
                                                    key={bank.bin}
                                                    value={bank.bin}
                                                    label={`${bank.shortName || bank.name} - ${bank.bin}`}
                                                >
                                                    <Space>
                                                        <img
                                                            src={bank.logo}
                                                            alt={bank.shortName}
                                                            style={{ width: 62, height: 20 }}
                                                        />
                                                        <span>{bank.shortName || bank.name}</span>
                                                        <Text type="secondary">({bank.bin})</Text>
                                                    </Space>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="acqId"
                                        label="Mã định danh ngân hàng (BIN)"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Vui lòng chọn ngân hàng để tự động điền",
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Tự động điền khi chọn ngân hàng"
                                            disabled
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="bank_name"
                                        label="Tên ngân hàng"
                                        rules={[
                                            { required: true, message: "Vui lòng chọn ngân hàng" },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Tự động điền khi chọn ngân hàng"
                                            disabled
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="template"
                                        label="Mẫu QR Code"
                                        rules={[
                                            { required: true, message: "Vui lòng chọn mẫu QR" },
                                        ]}
                                    >
                                        <Select placeholder="Chọn mẫu QR Code" disabled={selectedBankCode !== 'OCB'}>
                                            {templateOptions.map((temp) => (
                                                <Option key={temp.value} value={temp.value}>
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong>
                                                            <QrcodeOutlined /> {temp.label} - {temp.description}
                                                        </Text>
                                                    </Space>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Alert
                                message="Hướng dẫn"
                                description={
                                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                        <li>Chọn ngân hàng từ danh sách để tự động điền mã BIN</li>
                                        <li>Mã BIN được sử dụng để tạo mã QR thanh toán VietQR</li>
                                        <li>Chọn mẫu QR phù hợp với nhu cầu sử dụng của bạn</li>
                                    </ul>
                                }
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        </Card>

                        <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={() => navigate("/payment-account")}
                                        size="default"
                                        className="shadow-sm"
                                    >
                                        Quay lại
                                    </Button>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button onClick={() => navigate("/payment-account")}>
                                            Hủy
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            icon={<SaveOutlined />}
                                        >
                                            Tạo tài khoản
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}